/**
 * Utilidad para manejar la API de Síntesis de Voz del navegador.
 * Esta versión usa el SDK de Azure Speech cuando está disponible y fallback a Web Speech API.
 */
import { initializeSpeechSDK, synthesizeAndPlayText, stopSpeaking as azureStopSpeaking, getIsSpeaking as azureGetIsSpeaking, isSynthesisAvailable as azureIsSynthesisAvailable } from '@/utils/azureOpenAi/STS/TTS/azureTTS';
import MemoryConfigManager from '@/utils/azureOpenAi/config';
import { handleTTSError, Logger } from '@/utils/azureOpenAi/errorHandler';

// Sistema de eventos para notificar sobre el estado de la síntesis
type SpeakingEventHandler = (isSpeaking: boolean) => void;
const speakingEventHandlers: SpeakingEventHandler[] = [];

// Sistema de eventos para notificar sobre el estado de notificaciones
type NotifyEventHandler = (hasNotifications: boolean) => void;
const notifyEventHandlers: NotifyEventHandler[] = [];
let hasNotifications = false;

// Get speech configuration from centralized config
const configManager = MemoryConfigManager.getInstance();
const speechConfig = configManager.getAzureSpeechConfig();
// Variables para controlar el habla
const synth = window.speechSynthesis; // Fallback a Web Speech API
let isSpeaking = false;
let useFallbackSynthesis = false; // Usar Web Speech API como fallback
let initialized = false;

/**
 * Registra un manejador de eventos para el estado de la síntesis de voz
 */
export function onSpeakingStateChanged(handler: SpeakingEventHandler): () => void {
    speakingEventHandlers.push(handler);
    // Devolver función para eliminar el manejador
    return () => {
        const index = speakingEventHandlers.indexOf(handler);
        if (index !== -1) {
            speakingEventHandlers.splice(index, 1);
        }
    };
}

/**
 * Registra un manejador de eventos para el estado de las notificaciones
 */
export function onNotifyStateChanged(handler: NotifyEventHandler): () => void {
    notifyEventHandlers.push(handler);
    // Devolver función para eliminar el manejador
    return () => {
        const index = notifyEventHandlers.indexOf(handler);
        if (index !== -1) {
            notifyEventHandlers.splice(index, 1);
        }
    };
}

/**
 * Notifica a todos los manejadores sobre el cambio en el estado de síntesis
 */
function notifySpeakingStateChange(speaking: boolean): void {
    isSpeaking = speaking;
    speakingEventHandlers.forEach(handler => handler(speaking));
}

/**
 * Notifica a todos los manejadores sobre el cambio en el estado de notificaciones
 */
function notifyNotifyStateChange(notify: boolean): void {
    hasNotifications = notify;
    notifyEventHandlers.forEach(handler => handler(notify));
}

/**
 * Inicializa el sistema de síntesis de voz con Azure
 */
export function initializeTTS(apiKey?: string, region?: string): boolean {
    // Use provided params or fall back to centralized config
    const finalApiKey = apiKey || speechConfig.apiKey;
    const finalRegion = region || speechConfig.region;
    initialized = true;
    
    // Intentar inicializar el SDK de Azure Speech
    const azureInitialized = initializeSpeechSDK(finalApiKey, finalRegion);
    
    // Si falla, usaremos Web Speech API como fallback
    useFallbackSynthesis = !azureInitialized || speechConfig.useFallback;
    
    Logger.info('TTS', `Sistema TTS inicializado: ${azureInitialized ? 'Azure Speech SDK' : 'Web Speech API (fallback)'}`);
    return azureInitialized || !!synth;
}

/**
 * Habla el texto proporcionado usando la síntesis de voz.
 */
export function speak(options: { 
    text: string; 
    onEnd?: () => void; 
    onError?: (event: any) => void;
}): void {
    // Asegurar inicialización con valores por defecto si no se llamó a initializeTTS
    if (!initialized) {
        if (configManager.isSpeechConfigValid()) {
            initializeTTS();
        } else {
            // Forzar fallback si no hay credenciales
            useFallbackSynthesis = true;
            Logger.warn('TTS', 'No se encontraron credenciales válidas de Azure Speech, usando fallback');
        }
    }

    // Detener cualquier síntesis anterior
    stopSpeaking();
    
    // Si no hay texto, salir
    if (!options.text || options.text.trim() === '') {
        options.onEnd?.();
        return;
    }
    
    // Marcar que estamos hablando
    notifySpeakingStateChange(true);

    // Si usamos Azure Speech SDK
    if (!useFallbackSynthesis && initialized) {
        synthesizeAndPlayText(options.text, {
            onStart: () => {
                Logger.info('TTS', 'Azure TTS iniciado');
            },
            onEnd: () => {
                notifySpeakingStateChange(false);
                Logger.info('TTS', 'Azure TTS completado');
                options.onEnd?.();
            },
            onError: (error) => {
                notifySpeakingStateChange(false);
                const ttsError = handleTTSError(error, 'speak');
                Logger.error('TTS', 'Error en Azure TTS', ttsError);
                options.onError?.(error);
            }
        });
        return;
    }
    
    // Fallback a Web Speech API si no se puede usar Azure
    if (synth) {
        // Método Web Speech API estándar
        try {
            const utterance = new SpeechSynthesisUtterance(options.text);
            utterance.lang = 'es-ES';
            utterance.rate = 1.2;
            utterance.pitch = 1.0;
            utterance.volume = 1.0;

            // Intentar seleccionar una voz en español
            const voices = synth.getVoices();
            const spanishVoice = voices.find(voice => 
                voice.lang.startsWith('es') ||
                voice.name.toLowerCase().includes('spanish') ||
                voice.name.toLowerCase().includes('español')
            );
            
            if (spanishVoice) {
                utterance.voice = spanishVoice;
                Logger.info('TTS', `Usando voz: ${spanishVoice.name}`);
            }
            
            // Configurar callbacks
            utterance.onend = () => {
                notifySpeakingStateChange(false);
                Logger.info('TTS', 'Web Speech API TTS completado');
                options.onEnd?.();
            };
            
            utterance.onerror = (event) => {
                notifySpeakingStateChange(false);
                const webTTSError = handleTTSError(event, 'webSpeechAPI');
                Logger.error('TTS', 'Error en Web Speech API TTS', webTTSError);
                options.onError?.(event);
            };
            
            // Hablar
            synth.speak(utterance);
            Logger.info('TTS', 'Web Speech API TTS iniciado');
            
        } catch (error) {
            const initError = handleTTSError(error, 'webSpeechAPIInit');
            Logger.error('TTS', 'Error al iniciar Web Speech API', initError);
            notifySpeakingStateChange(false);
            options.onError?.(error);
        }
    } else {
        Logger.error('TTS', 'Síntesis de voz no disponible en este navegador');
        notifySpeakingStateChange(false);
        options.onError?.({ message: 'Síntesis de voz no disponible' });
    }
}

/**
 * Habla un fragmento de texto (usado para streaming)
 */
export function speakFragment(text: string): void {
    if (!text || text.trim() === '') return;
    
    // Si no estamos hablando, iniciar nueva síntesis
    if (!isSpeaking) {
        speak({ text });
        return;
    }
    
    // Azure no soporta bien fragmentos incrementales, así que acumulamos y hablamos completo
    // Las implementaciones avanzadas de fragmentos incrementales para Azure requieren
    // usar el API de bajo nivel con streams de audio
    Logger.debug('TTS', 'Fragmento recibido pero no soportado en modo incremental');
}

/**
 * Detiene la reproducción de voz actual.
 */
export function stopSpeaking(): void {
    // Si estamos usando Azure Speech
    if (!useFallbackSynthesis && initialized) {
        azureStopSpeaking();
    } 
    
    // Si estamos usando Web Speech API
    if (synth) {
        synth.cancel();
    }
    
    notifySpeakingStateChange(false);
    Logger.info('TTS', 'Síntesis detenida');
}

/**
 * Verifica si la síntesis de voz está actualmente activa.
 */
export function getIsSpeaking(): boolean {
    return !useFallbackSynthesis && initialized 
        ? azureGetIsSpeaking() 
        : isSpeaking;
}

/**
 * Verifica si la API de Síntesis de Voz está disponible.
 */
export function isSynthesisAvailable(): boolean {
    return !useFallbackSynthesis && initialized 
        ? azureIsSynthesisAvailable() 
        : !!synth;
}

/**
 * Obtiene el estado actual de las notificaciones
 */
export function getHasNotifications(): boolean {
    return hasNotifications;
}

/**
 * Establece el estado de las notificaciones y notifica a los listeners
 */
export function setNotifyState(notify: boolean): void {
    if (hasNotifications !== notify) {
        notifyNotifyStateChange(notify);
    }
}
